import React, { useState, useRef, useCallback, useImperativeHandle } from 'react';
import API from '../../api/axiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faRotateLeft, faRotateRight,
  faMagnifyingGlassPlus, faMagnifyingGlassMinus,
  faCrop, faArrowsLeftRight, faArrowsUpDown,
  faSquare, faExpand, faUndo, faEye, faPen,
} from '@fortawesome/free-solid-svg-icons';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import '../css/ImageApproval.css';
import { resolveEmsUploadPublicUrl, getPersistedEmsUploadRef } from '../../utils/emsPhotoUtils';
import { ACCREDITATION_S3_PREFIX } from '../../constants/accreditationUploads';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Lets users (and admins in ParticipantEdit) view and crop accreditation photos.
 *
 * Props:
 *   participantId    – participant record _id
 *   currentPhotoUrl  – already-resolved full URL (latest / cropped version)
 *   originalPhotoUrl – already-resolved full URL of the original upload (before any crop)
 *   onPhotoUpdated(newUrl) – called with the new full URL after a successful crop
 *   headless         – when true, render only the modals (no photo card); used when
 *                      buttons are already injected via portal into the Formio form
 */
const AccreditationPhotoManager = React.forwardRef(({ participantId, currentPhotoUrl, originalPhotoUrl, onPhotoUpdated, headless = false }, ref) => {
  const cropperRef = useRef(null);

  // null = closed; string = URL being viewed
  const [viewingUrl, setViewingUrl] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropError, setCropError] = useState(null);

  // Cropper tool state (mirrors ImageApproval behaviour)
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [flippedH, setFlippedH] = useState(false);
  const [flippedV, setFlippedV] = useState(false);
  const [activeTool, setActiveTool] = useState('crop');

  /* ── open / close — defined as useCallback so they can be exposed via ref ── */
  const openCrop = useCallback(() => {
    setRotation(0); setZoom(1); setAspectRatio(1);
    setFlippedH(false); setFlippedV(false); setActiveTool('crop');
    setCropError(null);
    setCropOpen(true);
  }, []);

  const closeCrop = useCallback(() => {
    setCropOpen(false);
    setCropError(null);
  }, []);

  /* Allow parent components to trigger the modals programmatically (e.g. from a portal button). */
  useImperativeHandle(ref, () => ({
    openCrop: () => { if (currentPhotoUrl) openCrop(); },
    openView: () => {
      if (!currentPhotoUrl) return;
      setViewingUrl(currentPhotoUrl);
    },
  }), [currentPhotoUrl, originalPhotoUrl, openCrop]);

  if (!participantId) return null;

  /* ── cropper controls ── */
  const handleRotateLeft = () => {
    cropperRef.current?.rotateImage(-90);
    setRotation(p => { const v = p - 90; return v < 0 ? v + 360 : v; });
  };
  const handleRotateRight = () => {
    cropperRef.current?.rotateImage(90);
    setRotation(p => (p + 90) % 360);
  };
  const handleZoomIn = () => {
    const next = Math.min(zoom + 0.1, 2.0);
    cropperRef.current?.zoomImage(next / zoom);
    setZoom(next);
  };
  const handleZoomOut = () => {
    const next = Math.max(zoom - 0.1, 0.5);
    cropperRef.current?.zoomImage(next / zoom);
    setZoom(next);
  };
  const handleZoomChange = (e) => {
    const next = parseFloat(e.target.value);
    if (next !== zoom) { cropperRef.current?.zoomImage(next / zoom); setZoom(next); }
  };
  const handleFlipHorizontal = () => {
    cropperRef.current?.flipImage(true, false);
    setFlippedH(p => !p);
  };
  const handleFlipVertical = () => {
    cropperRef.current?.flipImage(false, true);
    setFlippedV(p => !p);
  };
  const handleFineRotation = (e) => {
    const next = parseFloat(e.target.value);
    const diff = next - (rotation > 180 ? rotation - 360 : rotation);
    cropperRef.current?.rotateImage(diff);
    setRotation(next < 0 ? next + 360 : next);
  };
  const handleReset = () => {
    cropperRef.current?.reset();
    setRotation(0); setZoom(1); setAspectRatio(1);
    setFlippedH(false); setFlippedV(false); setActiveTool('crop');
  };

  /* ── save cropped photo ── */
  const handleSave = async () => {
    const canvas = cropperRef.current?.getCanvas();
    if (!canvas) { setCropError('Unable to extract image. Please try again.'); return; }

    setSaving(true);
    setCropError(null);
    try {
      const blob = await new Promise((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('Failed to create image blob')), 'image/png', 1.0)
      );
      const file = new File([blob], `profile-photo-${Date.now()}.png`, { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dir', ACCREDITATION_S3_PREFIX);

      // Shared instance attaches the Bearer token — /api/ems/uploads requires auth.
      const uploadRes = await API.post('/ems/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const persistedPhotoUrl = getPersistedEmsUploadRef(uploadRes.data);

      const participantRes = await API.get(`/ems/participants/${participantId}`);
      let personData = participantRes.data.person_data || {};
      if (typeof personData === 'string') {
        try { personData = JSON.parse(personData); } catch { personData = {}; }
      }
      const prev0 =
        Array.isArray(personData.photo) && personData.photo[0] && typeof personData.photo[0] === 'object'
          ? personData.photo[0] : {};

      await API.put(`/ems/participants/${participantId}`, {
        person_data: { ...personData, photo: [{ ...prev0, url: persistedPhotoUrl }] },
        // Crop flow: backend preserves original_url (pre-crop image) instead of overwriting it.
        admin_edit: true,
      });

      const newFullUrl = resolveEmsUploadPublicUrl(persistedPhotoUrl, API_URL);
      onPhotoUpdated(newFullUrl);
      closeCrop();
    } catch (err) {
      setCropError(err.response?.data?.message || err.message || 'Failed to save photo');
    } finally {
      setSaving(false);
    }
  };

  const hasPhoto = Boolean(currentPhotoUrl);
  // A crop has been applied when the two URLs differ (and both exist).
  const hasCrop = Boolean(currentPhotoUrl && originalPhotoUrl && currentPhotoUrl !== originalPhotoUrl);

  const THUMB = { width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '2px solid #dee2e6' };

  return (
    <>
      {/* ── Photo card (hidden in headless mode) ── */}
      {!headless && <div className="mb-3 p-3" style={{ background: '#f8f9fa', borderRadius: 10, border: '1px solid #dee2e6' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Identity Photo</div>

        {hasCrop ? (
          /* Side-by-side: original and current cropped */
          <div className="d-flex">
            {/* Original */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6c757d', letterSpacing: 1, marginBottom: 6 }}>ORIGINAL</div>
              <img
                src={originalPhotoUrl}
                alt="Original upload"
                onError={(e) => { e.currentTarget.src = '/images/avatar.png'; }}
                style={{ ...THUMB, marginBottom: 8, display: 'block', margin: '0 auto 5px' }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setViewingUrl(originalPhotoUrl)}
              >
                <FontAwesomeIcon icon={faEye} className="me-1" />View
              </button>
            </div>

            <div style={{ width: 1, alignSelf: 'stretch', background: '#dee2e6' }} />

            {/* Current / cropped */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#198754', letterSpacing: 1, marginBottom: 6 }}>CURRENT (CROPPED)</div>
              <img
                src={currentPhotoUrl}
                alt="Current accreditation"
                onError={(e) => { e.currentTarget.src = '/images/avatar.png'; }}
                style={{ ...THUMB, marginBottom: 8, display: 'block', margin: '0 auto 8px' }}
              />
              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setViewingUrl(currentPhotoUrl)}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />View
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: '#D9A128', color: '#fff', border: 'none' }}
                  onClick={openCrop}
                >
                  <FontAwesomeIcon icon={faPen} className="me-1" />Edit / Crop
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Single photo — no crop applied yet */
          <div className="d-flex align-items-center gap-3">
            <img
              src={currentPhotoUrl || '/images/avatar.png'}
              alt="Your accreditation"
              onError={(e) => { e.currentTarget.src = '/images/avatar.png'; }}
              style={{ ...THUMB, flexShrink: 0 }}
            />
            <div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setViewingUrl(currentPhotoUrl)}
                  disabled={!hasPhoto}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />View
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: '#D9A128', color: '#fff', border: 'none' }}
                  onClick={openCrop}
                  disabled={!hasPhoto}
                >
                  <FontAwesomeIcon icon={faPen} className="me-1" />Edit / Crop
                </button>
              </div>
              {!hasPhoto && (
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>
                  Upload a photo using the form below first.
                </div>
              )}
            </div>
          </div>
        )}
      </div>}

      {/* ── View modal ── */}
      {viewingUrl && (
        <div
          className="image-approval-modal-backdrop"
          onClick={() => setViewingUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="View photo"
        >
          <div
            style={{ position: 'relative', maxWidth: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={viewingUrl}
              alt="Full size accreditation"
              style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 8, display: 'block' }}
            />
            <button
              type="button"
              className="image-approval-modal-close"
              onClick={() => setViewingUrl(null)}
              aria-label="Close"
              style={{ position: 'absolute', top: -12, right: -12 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      )}

      {/* ── Crop modal ── */}
      {cropOpen && (
        <div
          className="image-approval-modal-backdrop"
          onClick={closeCrop}
          role="dialog"
          aria-modal="true"
          aria-label="Edit photo"
        >
          <div className="image-cropper-modal-content" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="image-cropper-modal-header">
              <h3 className="image-cropper-modal-title">Edit Your Photo</h3>
              <button type="button" className="image-approval-modal-close"
                onClick={closeCrop} aria-label="Close" disabled={saving}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Sidebar + Cropper */}
            <div className="image-cropper-main">
              <div className="image-cropper-sidebar">
                <button className={`sidebar-tool ${activeTool === 'crop' ? 'active' : ''}`}
                  onClick={() => setActiveTool('crop')} title="Crop" disabled={saving}>
                  <FontAwesomeIcon icon={faCrop} />
                </button>
                <button className={`sidebar-tool ${activeTool === 'flip' ? 'active' : ''}`}
                  onClick={() => setActiveTool('flip')} title="Flip" disabled={saving}>
                  <FontAwesomeIcon icon={faArrowsLeftRight} />
                </button>
                <button className={`sidebar-tool ${activeTool === 'ratio' ? 'active' : ''}`}
                  onClick={() => setActiveTool('ratio')} title="Aspect Ratio" disabled={saving}>
                  <FontAwesomeIcon icon={faSquare} />
                </button>
              </div>

              <div className="image-cropper-container">
                <Cropper ref={cropperRef} src={currentPhotoUrl}
                  className="image-cropper" stencilProps={{ aspectRatio }} />

                {activeTool === 'flip' && (
                  <div className="tool-panel flip-panel">
                    <button className={flippedH ? 'active' : ''} onClick={handleFlipHorizontal} disabled={saving}>
                      <FontAwesomeIcon icon={faArrowsLeftRight} /><span>Horizontal</span>
                    </button>
                    <button className={flippedV ? 'active' : ''} onClick={handleFlipVertical} disabled={saving}>
                      <FontAwesomeIcon icon={faArrowsUpDown} /><span>Vertical</span>
                    </button>
                  </div>
                )}

                {activeTool === 'ratio' && (
                  <div className="tool-panel ratio-panel">
                    <button className={aspectRatio === 1 ? 'active' : ''} onClick={() => setAspectRatio(1)} disabled={saving}>1:1</button>
                    <button className={aspectRatio === 4/3 ? 'active' : ''} onClick={() => setAspectRatio(4/3)} disabled={saving}>4:3</button>
                    <button className={aspectRatio === 16/9 ? 'active' : ''} onClick={() => setAspectRatio(16/9)} disabled={saving}>16:9</button>
                    <button className={aspectRatio === null ? 'active' : ''} onClick={() => setAspectRatio(null)} disabled={saving}>
                      <FontAwesomeIcon icon={faExpand} /><span>Free</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom toolbar */}
            <div className="image-cropper-toolbar">
              <button type="button" className="toolbar-btn" onClick={handleFlipHorizontal} disabled={saving} title="Flip Horizontal">
                <FontAwesomeIcon icon={faArrowsLeftRight} />
              </button>
              <button type="button" className="toolbar-btn" onClick={handleRotateLeft} disabled={saving} title="Rotate Left">
                <FontAwesomeIcon icon={faRotateLeft} />
              </button>
              <button type="button" className="toolbar-btn" onClick={handleRotateRight} disabled={saving} title="Rotate Right">
                <FontAwesomeIcon icon={faRotateRight} />
              </button>

              <div className="toolbar-group fine-rotation-group">
                <input type="range" min="-45" max="45" step="1"
                  value={rotation > 180 ? rotation - 360 : rotation}
                  onChange={handleFineRotation}
                  className="fine-rotation-slider" disabled={saving} />
                <span className="toolbar-value">{rotation}°</span>
              </div>

              <div className="toolbar-group">
                <button type="button" className="toolbar-btn" onClick={handleZoomOut}
                  disabled={saving || zoom <= 0.5} title="Zoom Out">
                  <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
                </button>
                <input type="range" min="0.5" max="2.0" step="0.1"
                  value={zoom} onChange={handleZoomChange}
                  className="toolbar-slider" disabled={saving} />
                <button type="button" className="toolbar-btn" onClick={handleZoomIn}
                  disabled={saving || zoom >= 2.0} title="Zoom In">
                  <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                </button>
                <span className="toolbar-value">{Math.round(zoom * 100)}%</span>
              </div>

              <button type="button" className="toolbar-btn" onClick={handleReset}
                disabled={saving} title="Reset">
                <FontAwesomeIcon icon={faUndo} />
              </button>
            </div>

            {cropError && <div className="image-cropper-error">{cropError}</div>}

            {/* Footer */}
            <div className="image-cropper-modal-footer">
              <div className="image-cropper-footer-actions">
                <button type="button" className="image-cropper-btn image-cropper-btn--cancel"
                  onClick={closeCrop} disabled={saving}>Cancel</button>
                <button type="button" className="image-cropper-btn image-cropper-btn--save"
                  onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default AccreditationPhotoManager;
